import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';

export interface GeneratedSqlPanelProps {
  open: boolean;
  onClose: () => void;
  /** Map of target_table → SQL string */
  sqlByTarget: Record<string, string>;
  loading?: boolean;
  error?: string | null;
}

/**
 * Phase 4.2: Modal showing generated SQL per target table.
 * Uses read-only Monaco editor with SQL syntax highlighting.
 */
const GeneratedSqlPanel: React.FC<GeneratedSqlPanelProps> = ({
  open,
  onClose,
  sqlByTarget,
  loading,
  error,
}) => {
  const tables = useMemo(() => Object.keys(sqlByTarget).sort(), [sqlByTarget]);
  const [activeTab, setActiveTab] = useState(0);
  const activeTable = tables[activeTab] ?? '';
  const activeSql = sqlByTarget[activeTable] ?? '';

  const handleCopy = () => {
    navigator.clipboard.writeText(activeSql);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, height: '70vh' } }}
    >
      <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16 }}>
            Generated SQL
          </Typography>
          {tables.length > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {tables.length} target{tables.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {tables.length > 1 && (
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 3,
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, fontSize: 12, textTransform: 'none', fontWeight: 600 },
          }}
        >
          {tables.map((t, i) => (
            <Tab key={t} label={t} value={i} />
          ))}
        </Tabs>
      )}

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {error ? (
          <Box sx={{ p: 3 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Generating SQL...</Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, mx: 3, my: 1, borderRadius: 2, overflow: 'hidden' }}>
            <Editor
              height="100%"
              language="sql"
              value={activeSql || '-- No SQL generated for this target'}
              theme="vs-dark"
              options={{
                readOnly: true,
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: '"Fira Code", "Roboto Mono", monospace',
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                padding: { top: 8 },
                scrollbar: { verticalScrollbarSize: 8 },
                renderLineHighlight: 'none',
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Tooltip title="Copy SQL to clipboard">
          <Button
            size="small"
            startIcon={<CopyIcon sx={{ fontSize: 16 }} />}
            onClick={handleCopy}
            disabled={!activeSql}
            sx={{ fontSize: 12, textTransform: 'none' }}
          >
            Copy
          </Button>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          onClick={onClose}
          sx={{ fontSize: 12, textTransform: 'none' }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeneratedSqlPanel;
